import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Sun, Moon } from "lucide-react";
import { useTheme } from "@/lib/ThemeContext";

const items = [
  { label: "Servizi", hash: "#servizi", tid: "nav-link-servizi" },
  { label: "Chi siamo", hash: "#chi-siamo", tid: "nav-link-chi-siamo" },
  { label: "Pacchetti", hash: "#pacchetti", tid: "nav-link-pacchetti" },
  { label: "Clienti", hash: "#clienti", tid: "nav-link-clienti" },
  { label: "The Transformation", hash: "#transformation", tid: "nav-link-transformation" },
  { label: "Contattaci", hash: "#contattaci", tid: "nav-link-contattaci" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggle } = useTheme();
  const isLight = theme === "light";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToAnchor = (e, hash) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + hash);
      return;
    }
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.2, 0.7, 0.2, 1] }}
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? isLight
            ? "backdrop-blur-xl bg-white/70 border-b border-black/10"
            : "backdrop-blur-xl bg-[#05050A]/70 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-[76px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img
            src="/assets/logo.png"
            alt="Logo Social Whale — balena stilizzata viola, agenzia di digital marketing a Cagliari"
            className="h-10 w-10 object-contain drop-shadow-[0_0_18px_rgba(147,51,234,0.55)] transition-transform duration-500 group-hover:scale-110"
          />
          <span className="font-display font-bold tracking-tight text-lg md:text-xl text-white">
            Social<span className="text-gradient">Whale</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {items.map((it) => (
            <a
              key={it.hash}
              href={it.hash}
              onClick={(e) => goToAnchor(e, it.hash)}
              data-testid={it.tid}
              className="nav-link text-sm font-medium"
            >
              {it.label}
            </a>
          ))}
        </nav>

        <a
          href="#contattaci"
          onClick={(e) => goToAnchor(e, "#contattaci")}
          data-testid="nav-cta-contattaci"
          className="hidden md:inline-flex btn-primary text-sm py-2.5 px-5"
        >
          Parliamone
        </a>

        <button
          type="button"
          data-testid="nav-theme-toggle"
          onClick={toggle}
          aria-label={isLight ? "Attiva tema scuro" : "Attiva tema chiaro"}
          title={isLight ? "Tema scuro" : "Tema chiaro"}
          className={`ml-3 hidden md:inline-flex h-10 w-10 items-center justify-center rounded-full border transition ${
            isLight
              ? "border-black/15 bg-black/[0.04] text-slate-700 hover:bg-black/[0.08]"
              : "border-white/15 bg-white/[0.04] text-slate-200 hover:bg-white/[0.08]"
          }`}
        >
          {isLight ? <Moon size={16} /> : <Sun size={16} />}
        </button>

        <button
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          className={`md:hidden p-2 rounded-lg ${isLight ? "text-slate-900 hover:bg-black/5" : "text-white/90 hover:bg-white/5"}`}
          onClick={() => setOpen((v) => !v)}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {open ? (
              <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
            ) : (
              <>
                <path d="M4 7h16" strokeLinecap="round" />
                <path d="M4 12h16" strokeLinecap="round" />
                <path d="M4 17h16" strokeLinecap="round" />
              </>
            )}
          </svg>
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className={`md:hidden border-t ${isLight ? "border-black/10 bg-white/95" : "border-white/10 bg-[#05050A]/95"} backdrop-blur-xl`}>
          <div className="px-6 py-6 flex flex-col gap-4">
            {items.map((it) => (
              <a
                key={it.hash}
                href={it.hash}
                data-testid={`m-${it.tid}`}
                onClick={(e) => goToAnchor(e, it.hash)}
                className={`${isLight ? "text-slate-800" : "text-slate-200"} text-base font-medium py-2`}
              >
                {it.label}
              </a>
            ))}
            <a
              href="#contattaci"
              onClick={(e) => goToAnchor(e, "#contattaci")}
              className="btn-primary mt-2"
              data-testid="m-nav-cta-contattaci"
            >
              Parliamone
            </a>
            <button
              type="button"
              onClick={toggle}
              data-testid="m-nav-theme-toggle"
              className={`mt-1 inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-sm border ${
                isLight ? "border-black/15 bg-black/[0.03] text-slate-800" : "border-white/15 bg-white/[0.03] text-slate-200"
              }`}
            >
              {isLight ? <Moon size={14} /> : <Sun size={14} />}
              {isLight ? "Tema scuro" : "Tema chiaro"}
            </button>
          </div>
        </div>
      )}
    </motion.header>
  );
}
